/**
 * Mounts session tracing and records a page view on every route change.
 *
 * Place once inside the router, alongside <PagePerfTracker />. Covers every
 * route including the unauthenticated ones — recording only starts once there
 * is a user and the `user_session_trace` gate is on.
 *
 * See docs/FeatureDesigns/user-actions.md.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isFeatureEnabled } from '../feature-gates';
import { API_BASE, useApi } from '../api';
import { normalizePageLabelWithTab } from './pageLabel';
import { isActive, resetSession, start, stop, recordPageView } from './userActionRecorder';

export interface UserActionTrackerProps {
  /** Undefined while signed out — tracing stays off. */
  userId?: string;
  /** Emitting app, e.g. `web-student`. */
  appSource: string;
  appVersion?: string;
}

export function UserActionTracker({ userId, appSource, appVersion }: UserActionTrackerProps) {
  const location = useLocation();
  const api = useApi();

  useEffect(() => {
    if (!userId || !isFeatureEnabled('user_session_trace')) {
      // Signing out (or the gate going off) closes the session rather than
      // dropping it — resetSession flushes what's pending first.
      if (isActive()) {
        resetSession('auth');
        stop();
      }
      return;
    }

    start(userId, {
      appSource,
      apiBase: API_BASE,
      getAuthHeaders: () => api.getAuthHeaders(),
      appVersion,
    });
  }, [userId, appSource, appVersion, api]);

  useEffect(() => {
    if (!isActive()) return;
    const path = `${location.pathname}${location.search}`;
    recordPageView(normalizePageLabelWithTab(location.pathname, location.search), path);
  }, [location.pathname, location.search, userId]);

  return null;
}
